<template>
  <BaseModal
    v-if="isOrganizationInformationModalOpen.status"
    :value="isOrganizationInformationModalOpen.status"
    :is-unique="isOrganizationInformationModalOpen.type === 'logo'"
    :modal-title="title"
    @close="reset"
  >
    <template #modal>
      <div class="d-flex flex-column justify-center">
        <BaseFileInput
          v-if="isOrganizationInformationModalOpen.type === 'logo'"
          ref="imageInput"
          :val="logo"
          truncate-length="50"
          append-icon="$mdiDownload"
          prepend-icon=""
          :placeholder="$t('organization.uploadLogo')"
          :clearable="false"
          outlined
          accept="image/jpeg,image/png,image/jpg"
          @change="imageChanged"
        />
        <template
          v-else-if="
            isOrganizationInformationModalOpen.type === 'lifetime_notification'
          "
        >
          <BaseTextField
            v-model="form.notification_lifetime"
            type="number"
            autofocus
            :label="`${$t('organization.notification_lifetime_label')} *`"
            :has-error="!!notificationLifetimeErrors"
            :error-messages="notificationLifetimeErrors"
            @change="$v.form.notification_lifetime.$touch()"
          />
        </template>
        <template
          v-else-if="isOrganizationInformationModalOpen.type === 'wizidee'"
        >
          <BaseTextField
            v-model="form.login"
            autofocus
            :label="`${$t('organization.wizidee_login')} *`"
            :has-error="!!loginErrors"
            :error-messages="loginErrors"
            @change="$v.form.login.$touch()"
          />
          <BaseTextField
            v-model="form.password"
            :label="`${$t('organization.wizidee_password')} *`"
            :has-error="!!passwordErrors"
            :error-messages="passwordErrors"
            @change="$v.form.password.$touch()"
          />
        </template>
        <template
          v-else-if="isOrganizationInformationModalOpen.type === 'yousign'"
        >
          <BaseTextField
            v-model="form.yousignApiKey"
            autofocus
            :label="`${$t('organization.yousign_apiKey')} *`"
            :has-error="!!yousignApiKeyErrors"
            :error-messages="yousignApiKeyErrors"
            @change="$v.form.yousignApiKey.$touch()"
          />
          <BaseTextField
            v-model="form.yousignWorkspaceId"
            :label="`${$t('organization.yousignWorkspaceId')} `"
          />
        </template>
        <BaseMarkdownEditor
          v-else
          v-model="payload"
          :placeholder="$t('organization.formDescription')"
        />
      </div>
    </template>
    <template #actions>
      <BaseButton
        text
        class="mx-2 ml-auto"
        type="secondary"
        color="#707080"
        @click="reset"
      >
        {{ $t("utils.cancel") }}
      </BaseButton>
      <BaseButton type="primary" :loading="isSubmitting" @click.stop="submit">
        {{ $t("folder.statusForm.validate") }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script>
import { validationMixin } from "vuelidate";
import { requiredIf } from "vuelidate/lib/validators";
import { mapState } from "vuex";
export default {
  name: "OrganizationInformationEditDialog",
  mixins: [validationMixin],
  data() {
    return {
      payload: null,
      displayDirectImage: false,
      logo: null,
      form: {
        login: "",
        password: "",
        yousignApiKey: "",
        yousignWorkspaceId: "",
        notification_lifetime: undefined,
      },
      isSubmitting: false,
    };
  },

  computed: {
    ...mapState({
      isOrganizationInformationModalOpen: (state) =>
        state.organization.isOrganizationInformationModalOpen,
      activeOrganization: (state) => state.organization.activeOrganization,
      loginErrors() {
        if (!this.$v.form.login.$dirty) return "";
        if (!this.$v.form.login.required)
          return this.$t("organization.wizidee_login_required");
        return "";
      },
      passwordErrors() {
        if (!this.$v.form.password.$dirty) return "";
        if (!this.$v.form.password.required)
          return this.$t("organization.wizidee_password_required");
        return "";
      },
      yousignApiKeyErrors() {
        if (!this.$v.form.yousignApiKey.$dirty) return "";
        if (!this.$v.form.yousignApiKey.required)
          return this.$t("organization.yousignApiKeyRequired");
        return "";
      },
      notificationLifetimeErrors() {
        if (!this.$v.form.notification_lifetime.$dirty) return "";
        if (!this.$v.form.notification_lifetime.required)
          return this.$t("utils.fieldIsRequired");
        if (!this.$v.form.notification_lifetime.positive)
          return this.$t("utils.must_be_positive_number");
        return "";
      },
    }),
    title() {
      switch (this.isOrganizationInformationModalOpen.type) {
        case "location":
          return this.$t("organization.location");
        case "description":
          return this.$t("organization.description");
        case "cosmoform":
          return this.$t("organization.PleaseSpecifyDefaultCosmoformMessage");
        case "wizidee":
          return this.$t("organization.wizideeConfiguration");
        case "yousign":
          return this.$t("organization.yousignConfiguration");
        case "lifetime_notification":
          return this.$t("organization.configure_notification_lifetime");
        default:
          return this.$t("organization.logo");
      }
    },
  },
  validations() {
    let validations = {};
    validations.form = {
      login: {
        required: requiredIf(
          () => this.isOrganizationInformationModalOpen.type === "wizidee"
        ),
      },
      password: {
        required: requiredIf(
          () => this.isOrganizationInformationModalOpen.type === "wizidee"
        ),
      },
      notification_lifetime: {
        required: requiredIf(
          () =>
            this.isOrganizationInformationModalOpen.type ===
            "lifetime_notification"
        ),
        positive: (value) => {
          if (
            this.isOrganizationInformationModalOpen.type ===
            "lifetime_notification"
          ) {
            return value >= 0;
          }
          return false;
        },
      },
      yousignApiKey: {
        required: requiredIf(
          () => this.isOrganizationInformationModalOpen.type === "yousign"
        ),
      },
    };

    return validations;
  },
  watch: {
    isOrganizationInformationModalOpen: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
    "activeOrganization.logo": {
      async handler() {
        if (!this.activeOrganization.logo) return;
        await this.getAvatar(this.activeOrganization.logo);
      },
      immediate: true,
    },
  },
  methods: {
    imageChanged(value) {
      this.payload = value;
    },
    async getAvatar(value) {
      const res = await fetch(value);
      const blob = await res.blob();
      const extension = blob.type.split("/");
      this.logo = new File([blob], `logo.${extension[1]}`, {
        type: blob.type,
      });
    },
    reset() {
      this.$store.commit(
        "organization/setIsOrganizationEditInformationModalOpen",
        { status: false }
      );
      this.payload = null;
      this.$v.form.$reset();
      this.form = {
        login: "",
        password: "",
        yousignApiKey: "",
        yousignWorkspaceId: "",
      };
    },
    async initialize() {
      switch (this.isOrganizationInformationModalOpen.type) {
        case "location":
          return (this.payload = this.activeOrganization.contact);
        case "description":
          return (this.payload = this.activeOrganization.description);
        case "cosmoform":
          return (this.payload =
            this.activeOrganization.cosmoformDefaultMessage);
        case "lifetime_notification":
          return (this.form.notification_lifetime =
            this.activeOrganization.lifetimeNotification);
        case "wizidee":
          this.form.login = this.activeOrganization.wizideeLogin;
          return (this.form.password = this.activeOrganization.password);
        case "yousign":
          this.form.yousignApiKey = this.activeOrganization.yousignApiKey;
          return (this.form.yousignWorkspaceId =
            this.activeOrganization.yousignWorkspaceId);
        default:
          return;
      }
    },
    async getPayload() {
      switch (this.isOrganizationInformationModalOpen.type) {
        case "location":
          return { contact: this.payload };
        case "description":
          return { description: this.payload };
        case "cosmoform":
          return { cosmoform_default_message: this.payload };
        case "wizidee":
          return {
            wizidee_login: this.form.login,
            wizidee_password: this.form.password,
          };
        case "yousign":
          return {
            yousign_api_key: this.form.yousignApiKey,
            yousign_workspace_id: this.form.yousignWorkspaceId,
          };
        case "lifetime_notification":
          return { notification_lifetime: this.form.notification_lifetime };
        default:
          return { logo: this.payload };
      }
    },

    async submit() {
      const payload = await this.getPayload();
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.isSubmitting = true;

      try {
        const response = await this.$store.dispatch(
          "organization/patchOrganization",
          {
            organizationId: this.$route.params.organizationId,
            payload,
          }
        );
        this.$store.commit("organization/setUpdateSpecificOrganization", {
          payload: response,
          organizationId: this.$route.params.organizationId,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.editSuccess"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.editError"),
          type: "ERROR",
        });
      }
      this.reset();
      this.isSubmitting = false;
    },
  },
};
</script>
